/**
 * AxelioLandingPage
 * 
 * Public-facing marketing landing page for Axelio SaaS platform.
 * Features:
 *   - Fixed top navigation with auth CTAs
 *   - Hero section with gradient background and floating stats card
 *   - Module selection grid (4 cards)
 *   - Services/tech specs section (3 cards)
 *   - Pricing table (3 tiers with Pro highlighted)
 *   - Footer
 * 
 * Design based on Material Design 3 color system with RTL support.
 */

import { useNavigate } from 'react-router-dom';
import { Package, Wallet, Users, TrendingUp, Cloud, Shield, Headphones, CircleCheck as CheckCircle2, CirclePlay as PlayCircle, ArrowLeft, ChartLine as LineChart, ShoppingBag, UserCircle } from 'lucide-react';

// ─── Module Login Options ─────────────────────────────────────────────────────

interface ModuleOption {
  id: string;
  label: string;
  labelHe: string;
  icon: React.ReactNode;
  redirectTo: string;
}

const MODULE_OPTIONS: ModuleOption[] = [
  {
    id: 'cashflow',
    label: 'Cash Flow',
    labelHe: 'תזרים מזומנים',
    icon: <LineChart className="w-4 h-4" />,
    redirectTo: '/app/cashflow',
  },
  {
    id: 'inventory',
    label: 'Inventory',
    labelHe: 'ניהול מלאי',
    icon: <Package className="w-4 h-4" />,
    redirectTo: '/app/inventory',
  },
  {
    id: 'employees',
    label: 'Employees',
    labelHe: 'ניהול עובדים',
    icon: <Users className="w-4 h-4" />,
    redirectTo: '/app/hr',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function AxelioLandingPage() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    // Scroll to pricing section where users can choose a module
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleModuleClick = (moduleId: string) => {
    const module = MODULE_OPTIONS.find(m => m.id === moduleId);
    if (module) {
      navigate('/login');
    } else {
      const pricingSection = document.getElementById('pricing-section');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleB2BLogin = () => {
    navigate('/b2b');
  };

  const handleEmployeeLogin = () => {
    navigate('/employee');
  };

  return (
    <div className="min-h-screen bg-[#f7f9fc] text-[#191c1e]" dir="rtl">
      {/* ═══════════════════════════════════════════════════════════════════
          TOP NAVIGATION BAR
      ═══════════════════════════════════════════════════════════════════ */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl transition-all duration-300 border-b border-slate-200/50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-4 md:py-6 flex justify-between items-center">
          {/* Logo - Right Side for RTL */}
          <div className="text-xl md:text-2xl font-bold tracking-tighter text-[#001949] order-3">
            Axelio
          </div>

          {/* Desktop Navigation Links - Center */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8 order-2">
            <a
              href="#solutions"
              className="font-semibold tracking-tight text-slate-600 hover:text-[#001949] transition-colors duration-300 border-b-2 border-transparent hover:border-[#001949] pb-1"
            >
              Solutions
            </a>
            <a
              href="#platform"
              className="font-semibold tracking-tight text-slate-600 hover:text-[#001949] transition-colors duration-300 border-b-2 border-transparent hover:border-[#001949] pb-1"
            >
              Platform
            </a>
            <a
              href="#resources"
              className="font-semibold tracking-tight text-slate-600 hover:text-[#001949] transition-colors duration-300 border-b-2 border-transparent hover:border-[#001949] pb-1"
            >
              Resources
            </a>
            <a
              href="#company"
              className="font-semibold tracking-tight text-slate-600 hover:text-[#001949] transition-colors duration-300 border-b-2 border-transparent hover:border-[#001949] pb-1"
            >
              Company
            </a>
          </div>

          {/* Auth Buttons - Left Side */}
          <div className="flex items-center gap-3 md:gap-4 order-1">
            {/* B2B Portal */}
            <button
              onClick={handleB2BLogin}
              className="flex items-center gap-1.5 px-3 md:px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#001949] transition-all"
            >
              <ShoppingBag className="w-4 h-4" />
              B2B
            </button>

            {/* Employee Portal */}
            <button
              onClick={handleEmployeeLogin}
              className="flex items-center gap-1.5 px-3 md:px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#001949] transition-all"
            >
              <UserCircle className="w-4 h-4" />
              עובדים
            </button>

            {/* Login */}
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-1.5 px-4 md:px-5 py-2 text-sm font-semibold text-slate-600 hover:text-[#001949] border border-slate-200 rounded-lg hover:border-[#001949] transition-all"
            >
              כניסה
            </button>

            {/* Get Started Button */}
            <button
              onClick={handleGetStarted}
              className="px-4 md:px-6 py-2 bg-[#001949] text-white rounded-lg font-bold text-sm md:text-base hover:bg-[#002c74] transition-all shadow-sm"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════════════════════
          HERO SECTION
      ═══════════════════════════════════════════════════════════════════ */}
      <header className="relative pt-24 md:pt-32 pb-20 md:pb-32 overflow-hidden bg-[#003087]">
        {/* Background Pattern/Gradient */}
        <div className="absolute inset-0 bg-gradient-to-bl from-[#0047AB] via-[#003087] to-[#002060] opacity-90" />
        
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Right: Dashboard Preview (shows first in RTL) */}
            <div className="relative order-1 lg:order-2">
              {/* Main Dashboard Frame */}
              <div className="relative">
                {/* Dashboard Container */}
                <div className="bg-[#1a2332] rounded-2xl shadow-2xl overflow-hidden border border-white/10">
                  <div className="aspect-[16/10] w-full bg-gradient-to-br from-[#1e293b] to-[#0f172a] flex items-center justify-center relative">
                    {/* Dashboard Content Placeholder */}
                    <div className="absolute inset-0 p-6 md:p-8">
                      {/* Top Bar */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-400/60" />
                          <div className="w-3 h-3 rounded-full bg-yellow-400/60" />
                          <div className="w-3 h-3 rounded-full bg-green-400/60" />
                        </div>
                      </div>
                      
                      {/* Chart Area */}
                      <div className="mt-8 space-y-4">
                        <div className="h-2 bg-blue-500/20 rounded-full w-3/4" />
                        <div className="h-2 bg-blue-500/15 rounded-full w-1/2" />
                        <div className="h-2 bg-blue-500/10 rounded-full w-2/3" />
                      </div>
                      
                      {/* Graph Visualization */}
                      <div className="mt-8 flex items-end gap-2 h-32">
                        <div className="flex-1 bg-blue-500/30 rounded-t" style={{ height: '40%' }} />
                        <div className="flex-1 bg-blue-500/40 rounded-t" style={{ height: '70%' }} />
                        <div className="flex-1 bg-blue-500/50 rounded-t" style={{ height: '55%' }} />
                        <div className="flex-1 bg-blue-500/60 rounded-t" style={{ height: '85%' }} />
                        <div className="flex-1 bg-blue-500/70 rounded-t" style={{ height: '100%' }} />
                        <div className="flex-1 bg-blue-500/60 rounded-t" style={{ height: '75%' }} />
                      </div>
                    </div>
                    
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Package className="w-16 h-16 text-white/10" />
                    </div>
                  </div>
                </div>

                {/* Floating Stats Card */}
                <div className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-2xl p-4 md:p-5 hidden md:block">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
                        Growth Rate
                      </div>
                      <div className="text-xl font-black text-slate-900">
                        124.5%+
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Left: Text Content (shows second in RTL) */}
            <div className="order-2 lg:order-1 text-center lg:text-right">
              {/* Headline */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6 md:mb-8">
                ניהול עסקי חכם
                <br />
                בעוצמה טכנולוגית
              </h1>

              {/* Subtitle */}
              <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto lg:mx-0 mb-8 md:mb-10 leading-relaxed font-medium">
                פלטפורמה מקצועית לניהול מלאי, תזרים מזומנים, כוח אדם ותקציב
                — הכל במקום אחד עם בינה עסקית מתקדמת.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 md:gap-6">
                <button
                  onClick={handleGetStarted}
                  className="px-8 py-4 bg-white text-[#003087] rounded-xl font-bold text-lg hover:bg-blue-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 duration-300"
                >
                  התחילו עכשיו
                </button>
                <button className="flex items-center gap-2 text-white font-semibold hover:text-blue-200 transition-all px-4 py-2">
                  <PlayCircle className="w-5 h-5" />
                  צפו בהדגמה
                </button>
              </div>
            </div>

          </div>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════════════════
          MODULE SELECTION SECTION
      ═══════════════════════════════════════════════════════════════════ */}
      <section id="solutions" className="py-16 md:py-32 bg-[#f7f9fc]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          {/* Section Header */}
          <div className="mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#001949] mb-3 md:mb-4">
              בחירת מערכת
            </h2>
            <p className="text-slate-600 text-base md:text-lg">
              בחר את המודול המתאים לצרכי הארגון שלך
            </p>
          </div>

          {/* Module Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {/* Module 1: Inventory */}
            <div
              onClick={() => handleModuleClick('inventory')}
              className="group p-6 md:p-8 bg-white rounded-xl hover:bg-[#001949] transition-all duration-500 cursor-pointer shadow-sm hover:shadow-2xl"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 bg-slate-100 rounded-lg flex items-center justify-center text-[#001949] mb-6 md:mb-8 group-hover:bg-white/20 group-hover:text-white transition-colors">
                <Package className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 group-hover:text-white transition-colors">
                ניהול מלאי
              </h3>
              <p className="text-slate-600 group-hover:text-blue-100 transition-colors mb-4 md:mb-6 text-sm md:text-base">
                מעקב מדויק אחר תנועות מלאי, ספקים והזמנות אוטומטיות.
              </p>
              <span className="text-[#001949] group-hover:text-white font-bold flex items-center gap-2 text-sm md:text-base">
                כניסה למערכת
                <ArrowLeft className="w-4 h-4" />
              </span>
            </div>

            {/* Module 2: Cash Flow */}
            <div
              onClick={() => handleModuleClick('cashflow')}
              className="group p-6 md:p-8 bg-white rounded-xl hover:bg-[#001949] transition-all duration-500 cursor-pointer shadow-sm hover:shadow-2xl"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 bg-slate-100 rounded-lg flex items-center justify-center text-[#001949] mb-6 md:mb-8 group-hover:bg-white/20 group-hover:text-white transition-colors">
                <Wallet className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 group-hover:text-white transition-colors">
                תזרים מזומנים
              </h3>
              <p className="text-slate-600 group-hover:text-blue-100 transition-colors mb-4 md:mb-6 text-sm md:text-base">
                חיזוי פיננסי, ניהול תקבולים ותשלומים בזמן אמת.
              </p>
              <span className="text-[#001949] group-hover:text-white font-bold flex items-center gap-2 text-sm md:text-base">
                כניסה למערכת
                <ArrowLeft className="w-4 h-4" />
              </span>
            </div>

            {/* Module 3: Employees */}
            <div
              onClick={() => handleModuleClick('employees')}
              className="group p-6 md:p-8 bg-white rounded-xl hover:bg-[#001949] transition-all duration-500 cursor-pointer shadow-sm hover:shadow-2xl"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 bg-slate-100 rounded-lg flex items-center justify-center text-[#001949] mb-6 md:mb-8 group-hover:bg-white/20 group-hover:text-white transition-colors">
                <Users className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 group-hover:text-white transition-colors">
                ניהול עובדים
              </h3>
              <p className="text-slate-600 group-hover:text-blue-100 transition-colors mb-4 md:mb-6 text-sm md:text-base">
                מערכת שעות, תיקי עובדים ודיווח נוכחות חכם.
              </p>
              <span className="text-[#001949] group-hover:text-white font-bold flex items-center gap-2 text-sm md:text-base">
                כניסה למערכת
                <ArrowLeft className="w-4 h-4" />
              </span>
            </div>

            {/* Module 4: Finance */}
            <div
              onClick={() => handleModuleClick('finance')}
              className="group p-6 md:p-8 bg-white rounded-xl hover:bg-[#001949] transition-all duration-500 cursor-pointer shadow-sm hover:shadow-2xl"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 bg-slate-100 rounded-lg flex items-center justify-center text-[#001949] mb-6 md:mb-8 group-hover:bg-white/20 group-hover:text-white transition-colors">
                <TrendingUp className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 group-hover:text-white transition-colors">
                כספים ותקציב
              </h3>
              <p className="text-slate-600 group-hover:text-blue-100 transition-colors mb-4 md:mb-6 text-sm md:text-base">
                ניתוח דוחות רווח והפסד ובניית תקציב שנתי מדויק.
              </p>
              <span className="text-[#001949] group-hover:text-white font-bold flex items-center gap-2 text-sm md:text-base">
                כניסה למערכת
                <ArrowLeft className="w-4 h-4" />
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          SERVICES & TECH SPECS
      ═══════════════════════════════════════════════════════════════════ */}
      <section id="platform" className="py-16 md:py-32 bg-[#f2f4f7]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {/* Cloud Support */}
          <div className="p-8 md:p-10 bg-white rounded-2xl flex flex-col items-center text-center shadow-sm">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-blue-50 flex items-center justify-center mb-6 md:mb-8">
              <Cloud className="w-8 h-8 md:w-10 md:h-10 text-[#001949]" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-[#001949] mb-3 md:mb-4">
              תמיכה בענן
            </h3>
            <p className="text-slate-600 leading-relaxed text-sm md:text-base">
              גישה מלאה מכל מקום בעולם, עם סנכרון נתונים מיידי בין כל המכשירים
              שלכם.
            </p>
          </div>

          {/* Military-Grade Security */}
          <div className="p-8 md:p-10 bg-white rounded-2xl flex flex-col items-center text-center shadow-sm">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-blue-50 flex items-center justify-center mb-6 md:mb-8">
              <Shield className="w-8 h-8 md:w-10 md:h-10 text-[#001949]" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-[#001949] mb-3 md:mb-4">
              אבטחה ברמה צבאית
            </h3>
            <p className="text-slate-600 leading-relaxed text-sm md:text-base">
              הנתונים שלכם מוגנים באמצעות הצפנה מתקדמת ופרוטוקולי אבטחה מחמירים
              ביותר.
            </p>
          </div>

          {/* Dedicated Support */}
          <div className="p-8 md:p-10 bg-white rounded-2xl flex flex-col items-center text-center shadow-sm">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-blue-50 flex items-center justify-center mb-6 md:mb-8">
              <Headphones className="w-8 h-8 md:w-10 md:h-10 text-[#001949]" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-[#001949] mb-3 md:mb-4">
              ליווי טכני ייעודי
            </h3>
            <p className="text-slate-600 leading-relaxed text-sm md:text-base">
              צוות המומחים שלנו זמין עבורכם לכל שאלה, תמיכה והדרכה שוטפת לאורך כל
              הדרך.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          PRICING SECTION
      ═══════════════════════════════════════════════════════════════════ */}
      <section id="pricing-section" className="py-16 md:py-32 bg-[#f7f9fc]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center">
          {/* Section Header */}
          <div className="mb-12 md:mb-20">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#001949] mb-3 md:mb-4">
              תוכניות מנוי
            </h2>
            <p className="text-slate-600 text-base md:text-lg">
              בחר את המסלול המדויק לצמיחת העסק שלך
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Basic Plan */}
            <div className="bg-[#f2f4f7] p-8 md:p-10 rounded-2xl text-right flex flex-col border border-transparent hover:border-blue-200 transition-all">
              <h4 className="text-base md:text-lg font-bold text-slate-500 mb-2">
                Basic
              </h4>
              <div className="flex items-baseline gap-1 mb-6 md:mb-8">
                <span className="text-3xl md:text-4xl font-black text-[#001949]">
                  299
                </span>
                <span className="text-lg md:text-xl font-bold text-[#001949]">
                  ₪
                </span>
                <span className="text-slate-500 text-sm mr-2">/ לחודש</span>
              </div>
              <ul className="space-y-3 md:space-y-4 mb-8 md:mb-12 flex-grow text-sm md:text-base">
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0" />
                  ניהול מלאי בסיסי
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0" />
                  עד 3 משתמשים
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0" />
                  תמיכה במייל
                </li>
              </ul>
              <button className="w-full py-3 md:py-4 bg-slate-200 text-[#001949] font-bold rounded-lg hover:bg-blue-100 transition-all">
                בחרו בייסיק
              </button>
            </div>

            {/* Pro Plan (Featured) */}
            <div className="relative bg-[#001949] text-white p-8 md:p-10 rounded-2xl text-right flex flex-col shadow-2xl md:scale-105 z-10">
              {/* Popular Badge */}
              <div className="absolute -top-3 md:-top-4 right-1/2 translate-x-1/2 px-3 md:px-4 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
                הנבחר ביותר
              </div>

              <h4 className="text-base md:text-lg font-bold text-blue-200 mb-2">
                Pro
              </h4>
              <div className="flex items-baseline gap-1 mb-6 md:mb-8">
                <span className="text-3xl md:text-4xl font-black">599</span>
                <span className="text-lg md:text-xl font-bold">₪</span>
                <span className="text-blue-200 text-sm mr-2">/ לחודש</span>
              </div>
              <ul className="space-y-3 md:space-y-4 mb-8 md:mb-12 flex-grow text-sm md:text-base">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-300 shrink-0" />
                  כל מודולי המערכת
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-300 shrink-0" />
                  עד 15 משתמשים
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-300 shrink-0" />
                  תמיכה טלפונית 24/7
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-300 shrink-0" />
                  דוחות בינה עסקית (BI)
                </li>
              </ul>
              <button
                onClick={handleGetStarted}
                className="w-full py-3 md:py-4 bg-white text-[#001949] font-bold rounded-lg hover:bg-blue-50 transition-all"
              >
                התחילו עכשיו
              </button>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-[#f2f4f7] p-8 md:p-10 rounded-2xl text-right flex flex-col border border-transparent hover:border-blue-200 transition-all">
              <h4 className="text-base md:text-lg font-bold text-slate-500 mb-2">
                Enterprise
              </h4>
              <div className="flex items-baseline gap-1 mb-6 md:mb-8">
                <span className="text-3xl md:text-4xl font-black text-[#001949]">
                  1299
                </span>
                <span className="text-lg md:text-xl font-bold text-[#001949]">
                  ₪
                </span>
                <span className="text-slate-500 text-sm mr-2">/ לחודש</span>
              </div>
              <ul className="space-y-3 md:space-y-4 mb-8 md:mb-12 flex-grow text-sm md:text-base">
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0" />
                  פתרון מותאם אישית
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0" />
                  ללא הגבלת משתמשים
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0" />
                  מנהל חשבון אישי
                </li>
                <li className="flex items-center gap-3 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0" />
                  הטמעה והדרכה באתר
                </li>
              </ul>
              <button className="w-full py-3 md:py-4 bg-slate-200 text-[#001949] font-bold rounded-lg hover:bg-blue-100 transition-all">
                צרו קשר
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          FOOTER
      ═══════════════════════════════════════════════════════════════════ */}
      <footer className="bg-slate-100 pt-16 md:pt-24 pb-8 md:pb-12">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="text-lg md:text-xl font-black text-[#001949] mb-3 md:mb-4">
              Axelio
            </div>
            <p className="text-slate-500 text-xs md:text-sm leading-relaxed">
              © 2024 Axelio. Precision Engineered.
              <br />
              העתיד של הניהול העסקי כבר כאן.
            </p>
          </div>

          {/* Solutions */}
          <div>
            <h5 className="font-bold text-[#001949] mb-4 md:mb-6 text-sm md:text-base">
              פתרונות
            </h5>
            <ul className="space-y-2 md:space-y-4">
              <li>
                <a
                  href="#"
                  className="text-slate-500 text-xs md:text-sm hover:text-[#002c74] transition-all"
                >
                  Modules
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-slate-500 text-xs md:text-sm hover:text-[#002c74] transition-all"
                >
                  Intelligence
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-slate-500 text-xs md:text-sm hover:text-[#002c74] transition-all"
                >
                  Security
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h5 className="font-bold text-[#001949] mb-4 md:mb-6 text-sm md:text-base">
              חברה
            </h5>
            <ul className="space-y-2 md:space-y-4">
              <li>
                <a
                  href="#"
                  className="text-slate-500 text-xs md:text-sm hover:text-[#002c74] transition-all"
                >
                  About Us
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-slate-500 text-xs md:text-sm hover:text-[#002c74] transition-all"
                >
                  Careers
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-slate-500 text-xs md:text-sm hover:text-[#002c74] transition-all"
                >
                  Analytics
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h5 className="font-bold text-[#001949] mb-4 md:mb-6 text-sm md:text-base">
              משפטי
            </h5>
            <ul className="space-y-2 md:space-y-4">
              <li>
                <a
                  href="#"
                  className="text-slate-500 text-xs md:text-sm hover:text-[#002c74] transition-all"
                >
                  Legal
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-slate-500 text-xs md:text-sm hover:text-[#002c74] transition-all"
                >
                  Privacy
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-slate-500 text-xs md:text-sm hover:text-[#002c74] transition-all"
                >
                  Terms
                </a>
              </li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
